import React from "react";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogFooter,
} from "../ui/dialog";
import { Info, AlertTriangle, Archive, Trash2 } from "lucide-react";

/**
 * A reusable confirmation modal that follows the application's design language.
 */
const ConfirmModal = ({
    isOpen,
    onClose,
    onConfirm,
    title = "Confirm Action",
    description = "Are you sure you want to proceed with this action?",
    confirmText = "Confirm",
    cancelText = "Cancel",
    loading = false,
    variant = "primary", // 'primary', 'danger', 'warning', 'info', 'archive'
    icon: CustomIcon = null,
}) => {

    // Determine variant styles
    const variantStyles = {
        primary: {
            bg: "bg-blue-100",
            text: "text-blue-600",
            button: "bg-[#55C5D0] hover:bg-[#4AADB7] focus:ring-[#55C5D0]",
            icon: Info
        },
        danger: {
            bg: "bg-red-100",
            text: "text-red-600",
            button: "bg-red-600 hover:bg-red-700 focus:ring-red-500",
            icon: Trash2
        },
        warning: {
            bg: "bg-yellow-100",
            text: "text-yellow-600",
            button: "bg-yellow-600 hover:bg-yellow-700 focus:ring-yellow-500",
            icon: AlertTriangle
        },
        archive: {
            bg: "bg-orange-100",
            text: "text-orange-600",
            button: "bg-orange-500 hover:bg-orange-600 focus:ring-orange-500",
            icon: Archive
        }
    };

    const style = variantStyles[variant] || variantStyles.primary;
    const Icon = CustomIcon || style.icon;

    return (
        <Dialog open={isOpen} onOpenChange={onClose}>
            <DialogContent className="sm:max-w-[425px] rounded-2xl border-none shadow-2xl">
                <DialogHeader>
                    <div className="flex items-center gap-4">
                        <div className={`p-3 rounded-2xl ${style.bg} ${style.text}`}>
                            <Icon className="w-6 h-6" />
                        </div>
                        <div>
                            <DialogTitle className="text-xl font-bold text-gray-900 leading-tight">
                                {title}
                            </DialogTitle>
                        </div>
                    </div>
                </DialogHeader>

                <div className="py-4">
                    <DialogDescription className="text-sm font-medium text-gray-500 leading-relaxed">
                        {description}
                    </DialogDescription>
                </div>

                <DialogFooter className="gap-3 sm:gap-0 sm:flex-row flex-col">
                    <button
                        onClick={onClose}
                        disabled={loading}
                        className="flex-1 px-6 py-3 text-xs font-black uppercase tracking-widest text-gray-500 bg-gray-100 border border-transparent rounded-xl hover:bg-gray-200 transition-all font-sans"
                    >
                        {cancelText}
                    </button>
                    <button
                        onClick={onConfirm}
                        disabled={loading}
                        className={`flex-1 px-6 py-3 text-xs font-black uppercase tracking-widest text-white rounded-xl transition-all font-sans shadow-lg active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 ${style.button}`}
                    >
                        {loading ? (
                            <>
                                <span className="w-3 h-3 border-2 border-white/20 border-t-white rounded-full animate-spin" />
                                Processing...
                            </>
                        ) : (
                            confirmText
                        )}
                    </button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

export default ConfirmModal;
